import React from "react";
import { tplBlocks } from "../../importGroups/tplBlocks";
import { isEqual } from 'lodash'
import {blockIsPopulatedConditions} from '../validation/blockIsPopulatedConditions'
import handleBlockDisplayConditions from '../handleBlockDisplayConditions'
import {useGetQueryData} from '../../utils/react-query-hooks/general'
import { __CheckAccessToModule } from '../accessControl'
import { useAnnotationEditPipelineContext } from '@/contexts/annotationEditPipelineContext'
import { useCollabEditContext } from '@/contexts/collabEditContext'
import { useCollabConditionalValueCleanup } from '@/utils/collab/useCollabConditionalValueCleanup'
import { CollabFieldWrapper } from '@/components/collab/CollabFieldWrapper'
// the ONE editable-block predicate, shared with okf-sub's server-side
// allowlist (loadContentDocument) so UI and flush can never disagree
import { isCollabEditableBlock } from '@okf/ootils'

import {
	temporaryMetaObjHack,
	formatContentTitle,
	isSiblingValueSameFn,
	identifyIfFailedFormValidation,
	runPropModifier,
	populateDefaultValue,
	checkEnableAIAnnotation
} from './functions'
import { usePrevious } from "../customHooks";
import styled from 'styled-components'
import {
  colors,
  SANS_3,
  SANS_2,
  PaddingTopBottom15,
  PaddingBottom20,
  PaddingBottom5,
  PaddingTopBottom10,
  PaddingTop5,
  PaddingTop20,
  getVal,
  deleteVal
} from "oolib";
import ErrorBoundary from "../../components/ErrorBoundary";
import { useAppSettingsContext } from "@/contexts/appSettingsContext";
import { useTCIContext } from "@/pageTpls/TCI/context";
import { blockRegistry } from "@okf/ootils";



const StyledConditionalWrapper = styled.div`
	padding-left: 1.5rem;
	padding-right: 1.5rem;
	background-color: ${colors.greyColor5};
`

function ContentBlock({
	props,
	value,
	metaObj,
	block,
	Wrapper : _Wrapper,
	Component,
	readOnly,
	key,
	isConditionalClassName,
	errorMsgs,
	subBlocksFormValidation,
	content,
	onChange,
	allowReadOnlyOnChange, // when true, onChange fires even in readOnly mode (used by hosts that route saves through a non-form pipeline, e.g. PublishedPage annotation edit)
	blockProps,
	memo,
	setFormValidation,
}) {

	const Wrappers = {
		div: 'div',
		PaddingTopBottom15,
		PaddingBottom20,
		PaddingTop20,
		PaddingBottom5,
		PaddingTop5,
		PaddingTopBottom10
	}
	
	// Wrapper can be passed as a Wrapper component or a string ( that should map to a component in the Wrappers object above )
	const Wrapper = typeof _Wrapper === 'string' ? Wrappers[_Wrapper] : _Wrapper
	
	let comp = (
		//i dont think blockIndexTracker is used for anything btw..
		<div className='blockIndexTracker' data-valuepath={block.valuePath}>
			<Wrapper
				id={`TplBlock_${block.props.id}`}
				className={`Tpl__block Tpl__block--${block.props.className}`}
			>
				<Component
					readOnly={readOnly}
					value={value} //value before props cuz sometimes we overwrite the value by updating blockprops using propModifiers
					{...(props || block.props)}
					className=''
					metaObj={readOnly && metaObj}
					content={content}
					errorMsgs={errorMsgs} /** wont exist if in ViewBlockGenerator */
					subBlocksFormValidation={subBlocksFormValidation} /** wont exist if in ViewBlockGenerator */
					onChange={(key, val, options ) => {
						if (!readOnly || allowReadOnlyOnChange) {
							onChange?.(block, val, options)
						}
					}}
					valuePath ={block.valuePath}
					pass
					memo={memo}
					passValidationErrorToFormValidation={(status) => {
						// console.log('pass validation fired', status);
						if(status === 'error'){
							setFormValidation &&
							setFormValidation(prev => ({
								...prev,
								invalidInputBlocks: [
									...((prev.invalidInputBlocks || []).filter(b => b.valuePath !== block.valuePath)),
									{ valuePath: block.valuePath }
								]
							}))
						}else{ //success
							setFormValidation &&
							setFormValidation(prev => ({
								...prev,
								invalidInputBlocks: prev.invalidInputBlocks.filter(b => b.valuePath !== block.valuePath)
							}))
						}
						
					}}
					onUploadStatusChange={(isUploading) => {
						setFormValidation &&
						setFormValidation(prev => ({
							...prev,
							uploadingBlocks: isUploading
								? [...(prev.uploadingBlocks || []).filter(b => b.valuePath !== block.valuePath), { valuePath: block.valuePath }]
								: (prev.uploadingBlocks || []).filter(b => b.valuePath !== block.valuePath)
						}))
					}}
					{...blockProps}
				/>

			</Wrapper>
		</div>
	)

	return (
		<ErrorBoundary key={block.props.id}>
			{isConditionalClassName
			? <StyledConditionalWrapper>{comp}</StyledConditionalWrapper>
			: comp }
		</ErrorBoundary>
		
		
	)
}

const checkContentBlockMemo = (prevProps, currentProps) => {
	const {
		value: prevVal,
		content: prevContent,
		block: prevBlock,
		errorMsgs: prevErrorMsgs,
		displayConditionsResult: prevDisplayConditionsResult,
		isInTCI: prevIsInTCI,  // Add this
		collabEditActive: prevCollabEditActive
	} = prevProps
	const {
		value: currentVal,
		content: currentContent,
		block: currentBlock,
		errorMsgs: currentErrorMsgs,
		displayConditionsResult: currentDisplayConditionsResult,
		isInTCI: currentIsInTCI,  // Add this
		collabEditActive: currentCollabEditActive
	} = currentProps

	const isValueSame = isEqual(prevVal, currentVal)

	// collab: a field lock flipping (readOnly true↔false) must re-render the
	// editor. No-op for legacy hosts, where readOnly never changes per block.
	const isReadOnlySame = prevProps.readOnly === currentProps.readOnly

	// collab: a lock acquire/release flips collabEditActive WITHOUT changing
	// readOnly (collab-editable blocks stay readOnly:false), so this is what
	// forces a re-render to swap in the fresh onChange when the lock frees.
	const isCollabEditActiveSame = prevCollabEditActive === currentCollabEditActive

	const isBlockPropsSame = !currentIsInTCI || isEqual(prevBlock.props, currentBlock.props)

	const isSiblingValueSame = isSiblingValueSameFn(
		currentBlock.props.siblingValuePath,
		prevContent,
		currentContent
	) /** a sibling input wholes value affects THIS. hence a sibling value change should rerender THIS */
	

	//prop modifier dependent value change detection
	let prevDependentValues, currentDependentValues;
	if(currentBlock.propModifiers){
		prevDependentValues = prevBlock.propModifiers.map(d => getVal( prevContent, d.targetValuePath, d.expectedValue))
		currentDependentValues = currentBlock.propModifiers.map(d => getVal( currentContent, d.targetValuePath, d.expectedValue))
		
	}
	
	// get prev dependent value by getting value against valpath from prev content
	// get this dependent value by getting value against valpath from this content
	// isPropModUnchanged = prevDependentValue === currentDependentValue
	const isPropModUnchanged = !prevDependentValues || prevDependentValues.every((prevValue,i) => currentDependentValues[i] === prevValue )
	const isShouldDisplaySame = prevDisplayConditionsResult === currentDisplayConditionsResult
	const isErrorMsgsSame = prevErrorMsgs === currentErrorMsgs

	const isIsRequiredSame = !currentIsInTCI || prevBlock.isRequired === currentBlock.isRequired
	let toReturn =  isValueSame && isReadOnlySame && isCollabEditActiveSame && isShouldDisplaySame && isErrorMsgsSame && isSiblingValueSame && isPropModUnchanged && isBlockPropsSame && isIsRequiredSame; ;
	return toReturn
}

const MemomizedContentBlock = React.memo(ContentBlock, checkContentBlockMemo)

function ViewBlockGenerator({
	block,
	Wrapper = `div`,
	content,
	blockProps,
	parentContent,
	hideHeading,
}) {


	const { APP_SETTINGS } = useAppSettingsContext();
	const activeLang = APP_SETTINGS.lang;

	const { tplConfig } = useTCIContext() || {};
	const isInTCI = !!tplConfig;

	// Get user from userData query for highlights ACL check
	const { user } = useGetQueryData('userData') || {};

	// Highlights/annotation ACL only applies to blocks that support annotation.
	// Instance-aware: the block is passed so the per-block kill-switch
	// (props.annotations.disable) makes this false — we then skip building the
	// annotation config below, so the editor mounts none of its annotation plugins.
	const supportsAnnotation = blockRegistry.hasCapability(block.comp, 'annotation', block);
	const canViewHighlights = supportsAnnotation
		? __CheckAccessToModule(user, 'READ', 'highlights')
		: true;
	const canCreateHighlights = supportsAnnotation
		? __CheckAccessToModule(user, 'PUBLISH', 'highlights')
		: true;

	// Collab editing (provided by the host page when the tenant's
	// _ContentCollabEditingMode is on and a live session is synced).
	// Editability is field-locked: focus acquires the (docId, valuePath) lock,
	// blur releases it; a field locked by someone else renders disabled with
	// the holder's name. Edits flow to the live Y.Doc (collabEdit.setField) —
	// never through the legacy save pipelines. Lexical included: the lock
	// holder edits text AND annotations inside the field; the emitted value
	// carries editorState + annoData and the server recomputes the
	// annotations.tags sidecar on flush.
	const collabEdit = useCollabEditContext();
	const collabBlockEditable = !!(collabEdit?.canEdit && isCollabEditableBlock(block));
	const collabLock = collabBlockEditable ? collabEdit.locks?.[block.valuePath] : undefined;
	const collabLockedByOther = !!(collabLock && collabLock.socketId !== collabEdit.socketId);
	const collabEditActive = collabBlockEditable && !collabLockedByOther;
	// This render IS the editing surface (collab tenants have no separate edit
	// form — the published page is the form). Gates the form-like behaviours
	// below: full displayConditions, inputOnly blocks, conditional styling,
	// hidden-value cleanup. Per-doc, unlike collabBlockEditable (per-block):
	// an instructions InfoText has no valuePath, so it is never block-editable,
	// but it must still show on the editing surface.
	const collabEditingSurface = !!collabEdit?.canEdit;

	// ─── COLLAB OPTION 2 (INTERIM — rip out when @lexical/yjs CollaborationPlugin
	// binds the editor to the Y.Doc) ────────────────────────────────────────────
	// Lexical is uncontrolled and memoized to ignore value-prop changes, so a
	// remote collaborator's edit reaches liveContribution but NOT the on-screen
	// editor. We force a remount (via a changing key) when THIS field's value
	// object changes while we are NOT the lock holder — only the holder edits, so
	// such a change is necessarily remote. Our own edits (we hold the lock) never
	// bump the key, so typing is never interrupted. Once the editor is bound to
	// the shared Y.Doc there is no separate value to desync and this whole block
	// (+ the key below) is deleted.
	const lexCollabRemountRef = React.useRef({ lastValue: undefined, version: 0 });

	// Pipeline for editing annotations on a non-editable doc (e.g. PublishedPage).
	// Provided by the host page when it wants this surface to allow direct annotation
	// edits — saves are routed to /quickUpdateContent with skipCreateChunksProducer.
	// DISABLED whenever a collab session is live (collabEdit non-null): in collab
	// mode annotation editing happens INSIDE the field lock via the collab flow —
	// this pipeline replaces the per-annotation-lock system there, and running
	// both would double-write (quickUpdate + flush) and desync the live Y.Doc.
	const annotationEditPipeline = useAnnotationEditPipelineContext();
	const annoEditOnReadOnlyEnabled = !!(
		!collabEdit && annotationEditPipeline && supportsAnnotation && canCreateHighlights
	);

	/**
   * STEPS:
   
   * 1.  Block Ref create
   * 2.  Get Value
   * 3.  Get Component
   * 4.  Format content title casing
   * 5.  Temporary Meta Object hack. ( will go soon )
   * 6.  Get customDisplayConditions result ( if they exist )
   * 7.  Final Verdict on should content block render
   * 		- is NOT inputOnly block
   * 		- block is populated with valid content
   * 		- displayConditions result is TRUE
   */

	
	
	const platformConfigs = useGetQueryData('platformConfigs') || {};
	const smTagTypesConfig = platformConfigs.SELF_MANAGED_BASE_CONFIGS?.annotation_tagTypesConfig || null;

	let props = {
		...block.props,
	}

	if (hideHeading) {
		const { label, sublabel, displayTitle, ...rest } = props;
		props = rest;
	}

	// Resolve annotation config for annotation-capable blocks.
	// SM hardcoded tagTypesConfig takes priority → then per-instance persisted → then empty.
	// For new blocks that don't have props.annotation at all, we construct it from scratch.
	if (blockRegistry.hasCapability(block.comp, 'annotation', block)) {
		props = {
			...props,
			annotation: {
				...(props.annotation || {}),
				tagTypesConfig: blockRegistry.getTagTypesConfig(block, smTagTypesConfig),
			}
		};
	}

	let value = block.valuePath && getVal( content, block.valuePath)


	if (block.propModifiers) {
		block.propModifiers.forEach(({targetValuePath, targetValuePathFn, expectedValue, propSideEffects}) => {
			runPropModifier({
				content,
				parentContent,
				props,
				targetValuePath,
				targetValuePathFn,
				expectedValue,
				propSideEffects,
				block
			})
		})
		// console.log({modifiedProps: props})
		}

	const Component = tplBlocks[block.comp]

	

	// formats the property 'kp_title' / 'kp_story_title' if it exists
	formatContentTitle({ block, value })

	// need to get rid of this completely.
	const metaObj = temporaryMetaObjHack({ block, content })


	/**
	 * Plain view pages evaluate displayConditions ONLY for InfoText (the
	 * chatbot 'is ai generated' usecase) — every other conditional block is
	 * hidden implicitly by the blockIsPopulated check below, because a
	 * condition that never showed never got a value. The collab editing
	 * surface breaks that implicit hiding (empty blocks must render to be
	 * editable), so THERE conditions are evaluated for every block, same as
	 * the edit form always did.
	 */
	const displayConditionsResult = block.displayConditions
		? (collabEditingSurface || block.comp === 'InfoText')
			? handleBlockDisplayConditions({content, block, parentContent})
			: true
		: true

	// Hidden-value cleanup (single-actor deletion of a value whose block just
	// flipped hidden) — see the hook for the full rules.
	useCollabConditionalValueCleanup({
		enabled: collabEditingSurface,
		collabEdit,
		block,
		content,
		displayConditionsResult,
	});

	const shouldContentBlockRender =
		// inputOnly blocks (form instructions etc.) belong wherever editing
		// happens — which on collab tenants is THIS page
		(!block.inputOnly || collabEditingSurface) &&
		!block.hideOnEditAndView && //new property (hidden) - purpose is to simply not show the block on the edit as well as view version of form, but apart from that, it acts like any other normal block and shows up everywhere it needs to
		// collab-editable blocks render even when empty — an unpopulated title
		// must still be an editable surface (Notion-style), unlike view pages
		// where empty fields are simply omitted. inputOnly blocks carry no
		// value at all, so on the editing surface they bypass the check too.
		(blockIsPopulatedConditions(block, value) || collabBlockEditable || (collabEditingSurface && block.inputOnly)) &&
		displayConditionsResult


	const contentBlockProps = {
		props,
		block,
		value,
		Wrapper: block.Wrapper || Wrapper,
		Component,
		// same grey conditional-question treatment the edit form gives — but
		// only on the editing surface; plain view pages stay unstyled
		isConditionalClassName:
			collabEditingSurface && block.displayConditions && !block.dontStyleAsConditional
				? '-conditional-'
				: '',
		metaObj,
		// Collab-editable blocks ALWAYS render their edit comp (readOnly:false) —
		// a lock held by someone else must not flip the field into its visually
		// different read-only rendering; the CollabFieldWrapper overlay blocks
		// interaction instead.
		readOnly: !collabBlockEditable,
		// Whether onChange routes to setField. Because readOnly stays false when
		// a collab lock flips, a lock acquire/release changes NONE of the other
		// memoized props — so without this in the memo comparison, the block
		// keeps a STALE onChange closure (captured while locked) after the lock
		// frees, and the (now-unlocked) field silently drops every edit. The
		// overlay lives outside the memo (CollabFieldWrapper) so it updated fine;
		// this is what keeps the handler in lockstep with it.
		collabEditActive,
		content,
		// Collab-editable + lock held (or free): live edits go to the Y.Doc.
		// Otherwise: when the host enabled the annotation-edit pipeline, route
		// the editor's onChange (originating from annotation create/edit/remove)
		// to the pipeline. ContentBlock honours allowReadOnlyOnChange to bypass
		// its default readOnly short-circuit on this onChange.
		onChange: collabEditActive
			? (changedBlock, val) => collabEdit.setField(changedBlock.valuePath, val)
			: annoEditOnReadOnlyEnabled
			? annotationEditPipeline.handleAnnotationChange
			: undefined,
		allowReadOnlyOnChange: annoEditOnReadOnlyEnabled,
		blockProps: {
			...blockProps,
			// Highlights ACL: control viewing and creating annotations
			...(!canViewHighlights && { disableViewHighlights: true }),
			...(!canCreateHighlights && { disableCreateHighlights: true }),
			// Unlocks the in-place lightbox actions + floating annotate button
			// inside LexicalTextEditor when readOnly is true.
			...(annoEditOnReadOnlyEnabled && { enableAnnotationEditOnReadOnly: true })
		},
		isInTCI
	}
	/**
	 * v. imp that the shouldContentBlockRender check is done here itself,
	 * rather than in the <ContentBlock/>
	 * else, the renderedCounter in the mapper function will not work.
	 */
	if (collabBlockEditable) {
		// INTERIM (see lexCollabRemountRef above): bump the remount key when a
		// non-holder sees this Lexical field's value change (= a remote edit).
		const isLexicalCollab = block.comp === "LexicalTextEditor";
		const collabIsHolder = !!(collabLock && collabLock.socketId === collabEdit.socketId);
		if (isLexicalCollab) {
			const tracker = lexCollabRemountRef.current;
			if (tracker.lastValue !== undefined && value !== tracker.lastValue && !collabIsHolder) {
				tracker.version += 1;
			}
			tracker.lastValue = value;
		}
		const collabRemountKey = isLexicalCollab
			? `collab-lex-v${lexCollabRemountRef.current.version}`
			: undefined;

		return (
			shouldContentBlockRender && (
				<CollabFieldWrapper
					valuePath={block.valuePath}
					lockedByOther={collabLockedByOther}
					lockHolderName={collabLock?.userName}
					lockHolderIsSelf={!!(collabLock?.userId && user?._id && collabLock.userId === user._id)}
					acquireLock={collabEdit.acquireLock}
					releaseLock={collabEdit.releaseLock}
				>
					<MemomizedContentBlock key={collabRemountKey} {...contentBlockProps} />
				</CollabFieldWrapper>
			)
		)
	}

	return (
		shouldContentBlockRender && <MemomizedContentBlock {...contentBlockProps} />
	)
}

function EditBlockGenerator({
	block,
	Wrapper = 'div',
	formValidation,
	setFormValidation,
	content,
	onChange,
	
	parentContent,

	/**
	 * in tci we dont want to memoize the contentblock
	 * so that we can an instant rerender.
	 * this flag is for that
	 */
	memo
}) {

	

	/**
	 * STEPS:
	 * 1.  Inject isRequired into 'props'
	 * 2.  Block Ref create
	 * 3.  Get Value
	 * 4.  Get Component
	 * 6.  Detect change in sibling value, and if so, reset THIS value ( community - paralegal dropdowns usecase in nct)
	 * 5.  Get displayConditions result
	 * 6.  Set isConditionalClassName to handle UI Styling of conditionals
	 * 7.  Final Verdict on should content block render
	 * 		- is NOT displayOnly block
	 * 		- displayConditions result is TRUE
	
	 */

	//richinput needs this. we dont want to do this calc inside rich input cuz then we wont be able to move
	//it to oolib ( cuz of react-query dependency of __GetContentTypeConfigNew )
	let { ai: { aiAnnotation: aiAnnotationConfig } = {} } = useGetQueryData('platformConfigs') || {}
	const { tplConfig } = useTCIContext() || {};
	const isInTCI = !!tplConfig;

	// Get user from userData query for highlights ACL check
	const { user } = useGetQueryData('userData') || {};

	// Highlights/annotation ACL only applies to blocks that support annotation.
	// Instance-aware: the block is passed so the per-block kill-switch
	// (props.annotations.disable) makes this false — we then skip building the
	// annotation config below, so the editor mounts none of its annotation plugins.
	const supportsAnnotation = blockRegistry.hasCapability(block.comp, 'annotation', block);
	const canViewHighlights = supportsAnnotation
		? __CheckAccessToModule(user, 'READ', 'highlights')
		: true;
	const canCreateHighlights = supportsAnnotation
		? __CheckAccessToModule(user, 'PUBLISH', 'highlights')
		: true;

	let prevContent = usePrevious(content)
	/**
	 * relevant in the case of repeaters,
	 * where sometimes, you wanna set display conditions to a repeater block,
	 * wrt. the 'parent content' within which this repeater's content resides 
	 */
	let prevParentContent = usePrevious(parentContent)
	
	

	if(!block.props){
		return <div style={{padding: '2rem', border: `1px solid ${colors.red}`, backgroundColor: colors.lightRed }}>
			<SANS_3 semibold>{`Block Name: ${block.comp}`}</SANS_3>
			<SANS_2>{'Some of the required props are missing'}</SANS_2>
		</div>
	}
	

	const shouldEnableAIAnnotation = checkEnableAIAnnotation({ aiAnnotationConfig, block })

	const platformConfigs_edit = useGetQueryData('platformConfigs') || {};
	const smTagTypesConfig_edit = platformConfigs_edit.SELF_MANAGED_BASE_CONFIGS?.annotation_tagTypesConfig || null;

	let props = {
		...block.props,
		isRequired: block.isRequired,
		enableAIAnnotation:shouldEnableAIAnnotation
	}

	// Resolve annotation config for annotation-capable blocks.
	// SM hardcoded tagTypesConfig takes priority → then per-instance persisted → then empty.
	if (blockRegistry.hasCapability(block.comp, 'annotation', block)) {
		props = {
			...props,
			annotation: {
				...(props.annotation || {}),
				tagTypesConfig: blockRegistry.getTagTypesConfig(block, smTagTypesConfig_edit),
			}
		};
	}





	let value = (block.valuePath || block.valuePath === null) && // null scenario is in tci display conditions, where the content at a given index itself is the value
		getVal( content, block.valuePath)


		// if(props.id==="main.raw2") return null;

	


	
	
	if(!isSiblingValueSameFn(props.siblingValuePath, prevContent, content)){
		value = undefined;
		deleteVal(content, block.valuePath)
	}

	const displayConditionsResult = block.displayConditions
		? handleBlockDisplayConditions({content, block, parentContent})
		: true

	const prevDisplayConditionsResult = block.displayConditions
		? handleBlockDisplayConditions({content: prevContent, block, parentContent: prevParentContent})
		: true

	//PENDING
	//if block has been hidden in this render iteration
	//and if a value exists against it
	//then delete the value that exists against it
	if(
		displayConditionsResult === false &&
		prevDisplayConditionsResult !== displayConditionsResult &&
		block.valuePath //this might be valueable in the future, cuz technically StaticRichText doesnt need a valuepath..
	){
		const hasValue = !!getVal( content, block.valuePath)
		if(hasValue) {
			deleteVal(content, block.valuePath)
		}
	}

	//used to set a default value against a path, 
	//if no val already exists against it.
	if(props.defaultValue){
		populateDefaultValue({
			content, 
			onChange, 
			block, 
			props
		})
		
	}
	
	// this literally changes the data in the props object passed into it.
	if (block.propModifiers) {
		block.propModifiers.forEach(({targetValuePath, targetValuePathFn, expectedValue, propSideEffects}) => {
			runPropModifier({
				content,
				parentContent,
				props,
				targetValuePath,
				targetValuePathFn,
				expectedValue,
				propSideEffects,
				block
			})
		})
		// console.log({modifiedProps: props})
		}

		const Component = tplBlocks[block.comp];
 
	if(!Component){
		//We dont want to throw an eror
		//but instead show a message saying this block doesnt exist.

		return <div style={{padding: '2rem', border: `1px solid ${colors.red}`, backgroundColor: colors.lightRed }}>
			<SANS_3 semibold>{`Block Name: ${block.comp}`}</SANS_3>
			<SANS_2>{'This block is currently incompatible with the TCI. Contact dev to get this fixed.'}</SANS_2>
		</div>
	}

	/**
	 * this classname renders the block with a grey background
	 * we want this for conditional questions.
	 * we DONT want this styling for the events conditionalities
	 *
	 * hence the below condition is required
	 */
	const isConditionalClassName =
		block.displayConditions &&
		!block.dontStyleAsConditional
		? '-conditional-'
		: ''

	

	const {errorMsgs, subBlocksFormValidation} = identifyIfFailedFormValidation(block, formValidation )
 
	const shouldContentBlockRender = !block.displayOnly && 
		!block.hideOnEditAndView && //new property (hidden) - purpose is to simply not show the block on the edit as well as view version of form, but apart from that, it acts like any other normal block and shows up everywhere it needs to
		displayConditionsResult

	const contentBlockProps = {
		props,
		content,
		block,
		value,
		Wrapper: block.Wrapper || Wrapper,
		Component,
		readOnly: false,
		isConditionalClassName,
		errorMsgs,
		subBlocksFormValidation, //for nested renderer comps like fragmentDisplayComps && repeaters
		onChange,
		memo, // we pass it through, only so that we can get the repeater comp to also follow this memo command if it exists.
		displayConditionsResult,
		setFormValidation,
		isInTCI,
		blockProps: {
			// Highlights ACL: control viewing and creating annotations
			...(!canViewHighlights && { disableViewHighlights: true }),
			...(!canCreateHighlights && { disableCreateHighlights: true })
		}
	}
	/**
	 * v. imp that the shouldContentBlockRender check is done here itself,
	 * rather than in the <ContentBlock/>
	 * else, the renderedCounter in the mapper function will not work.
	 */

	const _memo = props.memo!==undefined?props.memo:memo
	return (
		shouldContentBlockRender && 
		( _memo===false
		? <ContentBlock {...contentBlockProps} />
		: <MemomizedContentBlock {...contentBlockProps} />
		)
	)
}

export { EditBlockGenerator, ViewBlockGenerator };
